import { faXmark, faImage, faVideo, faFile, faFont, faPlus, faTrash, faEye } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { memo, useRef, useState, useEffect } from 'react';
import { getToken } from '../../../function/token';
import domain from '../../../../reusecomponent/domain';
import { ErrorToastContainer, WorningToastContainer } from '../../../function/CustomFunction';

// Create a simplified header component without complex handling - using default parameters instead of defaultProps
function Templateheader({ 
  handleChange = () => {}, 
  onFileChange = () => {}, 
  onTextChange = () => {}, 
  headerType = 'NONE',
  headerText = '', 
  variable = { data: [] }, 
  setvariable = () => {}, 
  exampleValues = {}, 
  setExampleValues = () => {} 
}) {
  const [header, setHeader] = useState(headerType || 'NONE');
  const [file, setFile] = useState(null);
  const [headertext, setHeadertext] = useState(headerText || '');
  const [fileError, setFileError] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipContent, setTooltipContent] = useState('');
  const [showVariableNotification, setShowVariableNotification] = useState(false);
  const [addedVariable, setAddedVariable] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef(null);

  // Extract variables from text
  const extractVariables = (text) => {
    const matches = text.match(/\{\{(\d+)\}\}/g) || [];
    let numbers = matches.map(v => parseInt(v.replace(/[{}]/g, ''))).sort((a, b) => a - b);
    
    // Ensuring uniqueness in numbers array
    numbers = [...new Set(numbers)];
    return numbers;
  };

  // Update variables when text changes
  const updateVariables = (text) => {
    if (setvariable) {
      const numbers = extractVariables(text);
      setvariable({ data: numbers });
      
      // Update example values if needed
      if (setExampleValues && exampleValues) {
        const newExampleValues = { ...exampleValues };
        let hasChanges = false;
        
        numbers.forEach(num => {
          // Only set a default value if the variable doesn't exist yet
          if (newExampleValues[num] === undefined) {
            newExampleValues[num] = `Value ${num}`;
            hasChanges = true;
          }
        });
        
        if (hasChanges) {
          setExampleValues(newExampleValues);
        }
      }
    }
  };

  const insertVariable = () => {
    const match = headertext.match(/\{\{(\d+)\}\}/g) || [];
    const numbers = match.map(v => parseInt(v.replace(/[{}]/g, '')));
    
    // Check if a variable already exists - only one variable is allowed in header
    if (numbers.length > 0) {
      ErrorToastContainer('Only one variable is allowed in header text');
      return;
    }
    
    const nextNumber = numbers.length ? Math.max(...numbers) + 1 : 1;

    const start = inputRef.current ? inputRef.current.selectionStart : 0;
    const end = inputRef.current ? inputRef.current.selectionEnd : 0;
    const before = headertext.substring(0, start);
    const after = headertext.substring(end);
    const updated = `${before}{{${nextNumber}}}${after}`;

    setHeadertext(updated);
    if (onTextChange) onTextChange(updated);
    updateVariables(updated);
    
    // Create default value for the new variable
    const defaultValue = `Value ${nextNumber}`;
    
    // Update example values with the default value
    if (setExampleValues && exampleValues) {
      const newExampleValues = { ...exampleValues };
      // Set the initial value but allow it to be completely deleted
      newExampleValues[nextNumber] = defaultValue;
      setExampleValues(newExampleValues);
    }
    
    // Show notification that variable was added
    setAddedVariable({
      number: nextNumber,
      value: defaultValue
    });
    setShowVariableNotification(true);
    
    // Set focus back to input and position cursor after the inserted variable
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        const newPosition = start + `{{${nextNumber}}}`.length;
        inputRef.current.setSelectionRange(newPosition, newPosition);
      }
    }, 0);
  };

  const handleHeaderTextChange = (e) => {
    const value = e.target.value;
    setHeadertext(value);
    if (onTextChange) onTextChange(value);
    updateVariables(value);
  };

  // Show tooltip for variable on hover
  const handleMouseMove = (e) => {
    if (!inputRef.current) return;
    
    const text = headertext;
    const cursorPos = getCursorPositionFromMouse(e);
    
    if (cursorPos === -1) {
      setShowTooltip(false);
      return;
    }
    
    // Check if cursor is on a variable
    const variableRegex = /\{\{(\d+)\}\}/g;
    let match;
    
    while ((match = variableRegex.exec(text)) !== null) {
      const start = match.index;
      const end = start + match[0].length;
      
      if (cursorPos >= start && cursorPos <= end) {
        const varNumber = match[1];
        const varValue = exampleValues?.[varNumber] || `Variable ${varNumber}`;
        
        setTooltipContent(`Variable {{${varNumber}}}: ${varValue}`);
        setTooltipPosition({ 
          x: e.clientX, 
          y: e.clientY - 30 // Position above cursor
        });
        setShowTooltip(true);
        return;
      }
    }
    
    setShowTooltip(false);
  };
  
  // Helper to get cursor position from mouse event
  const getCursorPositionFromMouse = (e) => {
    if (!inputRef.current) return -1;
    
    const input = inputRef.current;
    const inputRect = input.getBoundingClientRect();
    
    // If mouse is outside input, return -1
    if (
      e.clientX < inputRect.left || 
      e.clientX > inputRect.right || 
      e.clientY < inputRect.top || 
      e.clientY > inputRect.bottom
    ) {
      return -1;
    }
    
    // This is a simplified approach for single-line input
    const xPercent = (e.clientX - inputRect.left) / inputRect.width;
    const approxPos = Math.floor(xPercent * headertext.length);
    
    return Math.min(approxPos, headertext.length);
  };

  // Preview message with variables replaced
  const getPreviewText = () => {
    if (!headertext) return '';
    
    let previewText = headertext;
    
    if (exampleValues && Object.keys(exampleValues).length > 0) {
      Object.keys(exampleValues).forEach(key => {
        const variablePattern = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
        // Allow empty values, showing the placeholder if value is empty or undefined
        if (exampleValues[key] !== undefined && exampleValues[key] !== '') {
          previewText = previewText.replace(variablePattern, 
            `<span class="text-primary" style="font-weight: 500; padding: 0 2px; background-color: rgba(13, 110, 253, 0.1); border-radius: 2px;">${exampleValues[key]}</span>`);
        } else {
          // Keep the placeholder in the preview if the value is empty
          previewText = previewText.replace(variablePattern, 
            `<span style="opacity: 0.6; font-style: italic;">{{${key}}}</span>`);
        }
      });
    }
    
    return previewText;
  };

  const getFileAcceptType = () => {
    switch (header) {
      case 'IMAGE':
        return '.png,.jpg,.jpeg';
      case 'VIDEO':
        return '.mp4,.mov';
      case 'DOCUMENT':
        return '.pdf,.docx,.doc,.txt';
      default:
        return '';
    }
  };

  const getInputType = () => {
    return ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header)
      ? 'file'
      : null;
  };

  const validateFile = (selectedFile, type) => {
    setFileError('');
    
    if (!selectedFile) return false;
    
    const fileSizeMB = selectedFile.size / (1024 * 1024);
    
    if (type === 'IMAGE') {
      if (!selectedFile.type.match(/^image\/(jpeg|jpg|png)$/i)) {
        setFileError('Only JPG and PNG images are allowed');
        return false;
      }
      
      if (fileSizeMB > 5) {
        setFileError('Image must be 5MB or less');
        return false;
      }
    }
    
    if (type === 'VIDEO') {
      if (!selectedFile.type.match(/^video\/(mp4|quicktime)$/i)) {
        setFileError('Only MP4 and MOV videos are allowed');
        return false;
      }
      
      if (fileSizeMB > 16) {
        setFileError('Video must be 16MB or less');
        return false;
      }
    }
    
    if (type === 'DOCUMENT') {
      if (selectedFile.type !== 'application/pdf') {
        setFileError('Only PDF documents are allowed');
        return false;
      }
      
      if (fileSizeMB > 0.5) {
        setFileError('Document must be 500KB or less');
        return false;
      }
    }
    
    return true;
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    
    if (!selectedFile) {
      clearFile();
      return;
    }
    
    const isValid = validateFile(selectedFile, header);
    
    if (isValid) {
      try {
        setIsUploading(true); // Start loading state
        
        // Create FormData for file upload
        const formData = new FormData();
        formData.append('file', selectedFile);

        // Get token for authorization
        const access_token = getToken();
        
        // Upload file to API
        const response = await fetch(`${domain()}/uplode-file`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${access_token}`
          },
          body: formData
        });

        const result = await response.json();

        if (result.status) {
          // File uploaded successfully
          setFile(selectedFile);
          console.log("this is the result of file upload: ",result.data);
          
          
          if (onFileChange) {
            onFileChange(selectedFile, header, result.data);
          }
        } else {
          setFileError(result.message || 'Failed to upload file');
          if (inputRef.current) {
            inputRef.current.value = '';
          }
        }
      } catch (error) {
        setFileError('Error uploading file. Please try again.');
        if (inputRef.current) {
          inputRef.current.value = '';
        }
      } finally {
        setIsUploading(false); // End loading state
      }
    } else {
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  };

  const handleHeaderTypeChange = (e) => {
    const selectedType = e.target.value;
    
    setHeader(selectedType);
    setFileError('');
    
    if (selectedType !== 'TEXT') {
      setHeadertext('');
      if (onTextChange) onTextChange('');
      if (setvariable) setvariable({ data: [] });
    }
    
    if (!['IMAGE', 'VIDEO', 'DOCUMENT'].includes(selectedType)) {
      clearFile();
    }
    
    if (onFileChange && selectedType !== 'TEXT') {
      onFileChange(null, selectedType);
    }

    if (handleChange) {
      handleChange(selectedType);
    }
  };

  const clearFile = () => {
    if (inputRef.current) inputRef.current.value = '';
    setFile(null);
    setFileError('');
    
    if (onFileChange) onFileChange(null, header);
  };

  const handleClear = () => {
    if (header === 'TEXT') {
      setHeadertext('');
      if (onTextChange) onTextChange('');
      if (setvariable) setvariable({ data: [] });
    } else {
      clearFile();
    }
  };

  const getFileTypeIcon = () => {
    switch (header) {
      case 'IMAGE':
        return faImage;
      case 'VIDEO':
        return faVideo;
      case 'DOCUMENT':
        return faFile;
      default:
        return faFont;
    }
  };

  const getHeaderTypeName = () => {
    switch (header) {
      case 'IMAGE':
        return 'Image';
      case 'VIDEO':
        return 'Video';
      case 'DOCUMENT':
        return 'Document';
      default:
        return 'None';
    }
  };

  const inputType = getInputType();
  const acceptType = getFileAcceptType();
  const hasVariables = variable?.data?.length > 0;

  // Sync internal state with props for edit mode
  useEffect(() => {
    setHeader(headerType || 'NONE');
  }, [headerType]);

  useEffect(() => {
    setHeadertext(headerText || '');
  }, [headerText]);

  return (
    <div className="row pt-2 pr-0">
      <div className="form-group col-lg-12">
        <label className="d-flex align-items-center">
          Header <span className="text-secondary ml-1">* Optional</span>
        </label>
        
        <select
          className="form-control mb-3"
          onChange={handleHeaderTypeChange}
          value={header}
          style={{ cursor: 'pointer' }}
          disabled={isUploading}
        >
          <option value="NONE">None</option>
          <option value="TEXT">Text</option>
          <option value="IMAGE">Image</option>
          <option value="VIDEO">Video</option>
          <option value="DOCUMENT">Document</option>
        </select>

        {header === 'TEXT' && (
          <div className="form-group">
            <label htmlFor="headerTextInput">Header Text</label>
            <div className="mb-2">
              <div className="position-relative message-editor-container">
                <input
                  id="headerTextInput"
                  ref={inputRef}
                  type="text"
                  className="form-control custom-textarea"
                  value={headertext}
                  onChange={handleHeaderTextChange}
                  placeholder="Enter header text"
                  maxLength={60}
                  style={{
                    cursor: 'text',
                    border: headertext.includes('{{') ? '2px solid #6366F1' : '1px solid #ced4da',
                    borderRadius: '8px',
                    padding: '0.625rem 2rem 0.625rem 0.75rem',
                    boxShadow: headertext.includes('{{') ? '0 0 0 3px rgba(99, 102, 241, 0.15)' : 'none',
                    fontSize: '1rem',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={() => setShowTooltip(false)}
                  autoFocus
                />
                {headertext && (
                  <button
                    className="btn btn-sm position-absolute"
                    type="button"
                    onClick={handleClear}
                    style={{
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'transparent',
                      fontSize: '1rem',
                      color: '#6c757d',
                      opacity: '0.7',
                      padding: '0.25rem',
                      transition: 'opacity 0.2s ease'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                    onMouseOut={(e) => e.currentTarget.style.opacity = '0.7'}
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                )}
                <div className="text-right mt-1">
                  <small className="text-muted">
                    {headertext.length}/60
                  </small>
                </div>
                
                {showTooltip && (
                  <div 
                    className="variable-tooltip bg-dark text-white p-2 rounded"
                    style={{
                      position: 'fixed',
                      top: tooltipPosition.y + 'px',
                      left: tooltipPosition.x + 'px',
                      transform: 'translate(-50%, -100%)',
                      zIndex: 1000,
                      fontSize: '0.8rem',
                      pointerEvents: 'none',
                      boxShadow: '0 3px 6px rgba(0,0,0,0.16)'
                    }}
                  >
                    {tooltipContent}
                  </div>
                )}
              </div>
            </div>
            
            <div className="d-flex align-items-center formatting-toolbar mb-3">
              <button 
                type="button" 
                className={`btn btn-sm ${variable.data.length > 0 ? 'btn-outline-secondary disabled' : 'btn-outline-primary'}`}
                onClick={insertVariable}
                disabled={variable.data.length > 0}
                title={variable.data.length > 0 ? 'Only one variable is allowed in header' : 'Add a variable to the header text'}
              >
                <FontAwesomeIcon icon={faPlus} className="mr-1" />
                Add Variable
                {variable.data.length > 0 && <span className="ml-1 badge badge-secondary">Limit reached</span>}
              </button>
              
              {variable.data.length === 0 && (
                <small className="ml-2 text-muted">
                  <FontAwesomeIcon icon={faEye} className="mr-1" />
                  You can add up to 1 variable in header text
                </small>
              )}
              
              {hasVariables && (
                <button 
                  type="button" 
                  className="btn btn-sm btn-outline-secondary ml-2"
                  style={{
                    borderRadius: '4px',
                    fontWeight: '500',
                    padding: '0.5rem 1rem'
                  }}
                  onClick={() => {
                    // Show preview of the message with variables replaced
                    const previewText = getPreviewText();
                    WorningToastContainer(`Preview: ${previewText.replace(/<[^>]*>/g, '')}`);
                  }}
                >
                  <FontAwesomeIcon icon={faEye} className="mr-2" />
                  Preview
                </button>
              )}
            </div>
            
            {showVariableNotification && addedVariable && (
              <div 
                className="alert mt-3"
                style={{
                  animation: 'fadeIn 0.3s',
                  padding: '0.75rem 1rem',
                  borderLeft: '4px solid #6366F1',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(99, 102, 241, 0.05)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  border: '1px solid rgba(99, 102, 241, 0.2)'
                }}
              >
                <div className="d-flex align-items-center mb-2">
                  <div style={{ fontWeight: '600', color: '#6366F1' }}>
                    Variable Added: <span style={{ 
                      backgroundColor: 'rgba(99, 102, 241, 0.1)', 
                      padding: '2px 6px', 
                      borderRadius: '4px', 
                      fontFamily: 'monospace'
                    }}>{`{{${addedVariable.number}}}`}(Maximum 1 variable allowed)</span>
                  </div>
                  <button 
                    type="button" 
                    className="close ml-auto" 
                    onClick={() => setShowVariableNotification(false)}
                    style={{ 
                      marginLeft: 'auto',
                      fontSize: '1.25rem',
                      color: '#6c757d'
                    }}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                
                <div className="mt-1 mb-2">
                  <div className="d-flex align-items-center">
                    <div style={{ width: '120px', fontSize: '0.85rem', color: '#6c757d' }}>
                      <strong>Sample Value:</strong>
                    </div>
                    <div className="flex-grow-1">
                      <input 
                        type="text" 
                        className="form-control" 
                        value={addedVariable.value !== undefined ? addedVariable.value : ""}
                        onChange={(e) => {
                          // Update the example value when user edits it
                          if (setExampleValues && exampleValues) {
                            const newExampleValues = { ...exampleValues };
                            newExampleValues[addedVariable.number] = e.target.value;
                            setExampleValues(newExampleValues);
                            
                            // Update the displayed variable value
                            setAddedVariable({
                              ...addedVariable,
                              value: e.target.value
                            });
                          }
                        }}
                        placeholder="Enter value for this variable"
                        style={{
                          borderColor: '#d1d5db',
                          boxShadow: 'none',
                          transition: 'all 0.2s ease'
                        }}
                        autoFocus
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {inputType === 'file' && (
          <div className="form-group">
            <label>Upload {getHeaderTypeName()}</label>
            <div className="input-group p-0 border rounded-0">
              <div className="input-group-prepend">
                <span className="input-group-text">
                  <FontAwesomeIcon icon={getFileTypeIcon()} />
                </span>
              </div>
              <div className="custom-file">
                <input
                  ref={inputRef}
                  type="file"
                  accept={acceptType}
                  className="custom-file-input"
                  id="headerFileInput"
                  onChange={handleFileChange}
                  disabled={isUploading}
                />
                <label className="custom-file-label" htmlFor="headerFileInput">
                  {isUploading ? 'Uploading...' : (file ? file.name : `Choose ${getHeaderTypeName().toLowerCase()}`)}
                </label>
              </div>
              <div className="input-group-append">
                <button
                  className="px-2 border"
                  type="button"
                  onClick={handleClear}
                  disabled={isUploading}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
            </div>
            
            <small className="form-text text-muted mb-2">
              {header === 'IMAGE' && 'Image must be JPG or PNG, 5MB or less'}
              {header === 'VIDEO' && 'Video must be MP4 or MOV, 16MB or less, max 30 seconds'}
              {header === 'DOCUMENT' && 'Document must be PDF, 500KB or less'}
            </small>
            
            {fileError && (
              <div className="alert alert-danger py-2 mt-2" role="alert">
                <small>{fileError}</small>
              </div>
            )}

            {isUploading && (
              <div className="alert alert-info py-2 mt-2" role="alert">
                <small>Please wait while your file is being uploaded...</small>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default memo(Templateheader);
